import React, {Fragment} from 'react'
import {connect} from 'react-redux'
import {string, bool, func, number, oneOf, shape, arrayOf} from 'prop-types'

import {requestNewCode, switchLoginMode} from '../login.actionCreators'
import {loginModes, loginModesList, language} from '../helpers/login.data'
import localeData from '../helpers/login.lang'

import AuthButton from './authButton/authButton.presenter'
import HelperLink from './helperLink/helperLink.presenter'
import {isNotSecretCode} from '../welcomeSection/welcomeSection.helper'
import {isModeOneOf} from '../login.helper'

const locale = localeData[language]
const {forgotPassword, forgotPasswordEmailSent, normal, tfaCode, audit, newUserLinkExpired, forgottenPasswordLinkExpired} = loginModes

const ActionSection = ({isProduction, username, authFailedTimes, codeSendMessage, codeSendDelay, mode, onRequestNewCode, onSwitchMode, isAuthInProgress, isPasswordPolicyPending, canRequestNewCode, onSubmit, password, secretCode, ssoInformation}) => (
  isProduction
    ? (
      <div className='loginButtonOuterWrapper'>
        <AuthButton
          isVisible={!isModeOneOf({mode, modes: [forgotPasswordEmailSent, newUserLinkExpired]})}
          isProduction={isProduction}
          onSubmit={onSubmit}
          username={username}
          password={password}
          secretCode={secretCode}
        />
        <div className='prodForgotPassword'>
          <HelperLink
            isVisible={mode === audit}
            caption={locale.auditCancel()}
            onClick={() => onSwitchMode(normal)}
            uniqueID='cancelLDAPAudit'
          />
          <HelperLink
            isVisible={mode === normal}
            caption={locale.iForgotMyPassword()}
            onClick={() => !isAuthInProgress && !isPasswordPolicyPending && onSwitchMode(forgotPassword)}
            isDisabled={isAuthInProgress}
            isEmphasised={authFailedTimes >= 3}
            uniqueID='forgotPasswordHelperLink'
          />
          <HelperLink
            isVisible={mode === forgotPassword || mode === forgotPasswordEmailSent}
            caption={locale.iRememberMyPassword()}
            onClick={() => !isAuthInProgress && !isPasswordPolicyPending && onSwitchMode(normal)}
            isDisabled={isAuthInProgress}
            uniqueID='backToLoginHelperLink'
          />
          <HelperLink
            isVisible={ssoInformation.ssoEnabled}
            caption={locale.goToSsoLogin()}
            href={ssoInformation.ssoClients ? ssoInformation.ssoClients.clientRegistrations[0].fromOidcIssuerLocation : ''}
            isDisabled={isAuthInProgress}
            uniqueID='ssoRedirectHelperLink'
          />
        </div>
      </div>
    )
    : (
      <div className='callToActionContainer'>
        <div className='secondaryActionWrapper'>
          <AuthButton
            isVisible={!isModeOneOf({mode, modes: [forgotPasswordEmailSent, newUserLinkExpired]})}
            isProduction={isProduction}
            onSubmit={onSubmit}
            username={username}
            password={password}
            secretCode={secretCode}
          />
          <HelperLink
            isVisible={mode === normal}
            caption={locale.iForgotMyPassword()}
            onClick={() => !isAuthInProgress && !isPasswordPolicyPending && onSwitchMode(forgotPassword)}
            isDisabled={isAuthInProgress}
            isEmphasised={authFailedTimes >= 3}
          />
          {canRequestNewCode && (codeSendDelay === 0) && (codeSendMessage !== 'USER_PENALIZED') && (
            <Fragment>
              <HelperLink
                isVisible={mode === tfaCode}
                caption={locale.sendNewCode()}
                onClick={() => !isAuthInProgress && !isPasswordPolicyPending && !codeSendDelay && onRequestNewCode({username})}
                isDisabled={isAuthInProgress || (codeSendDelay > 0)}
              />
            </Fragment>
          )}
          {isNotSecretCode({canRequestNewCode, codeSendDelay, codeSendMessage}) && (
            <div className='newCodeSentMessage'>{locale.newCodeSentMessage()}</div>
          )}
          <HelperLink
            isVisible={mode === forgotPassword || mode === forgotPasswordEmailSent}
            caption={locale.iRememberMyPassword()}
            onClick={() => !isAuthInProgress && !isPasswordPolicyPending && onSwitchMode(normal)}
            isDisabled={isAuthInProgress}
          />
          <HelperLink
            isVisible={mode === audit}
            caption={locale.auditCancel()}
            onClick={() => onSwitchMode(normal)}
            uniqueID='cancelLDAPAudit'
          />
          <HelperLink
            isVisible={isModeOneOf({mode, modes: [newUserLinkExpired, forgottenPasswordLinkExpired]})}
            caption={locale.goToLogin()}
            onClick={() => onSwitchMode(normal)}
            uniqueID='cancelExpired'
          />
          <HelperLink
            isVisible={ssoInformation.ssoEnabled}
            caption={locale.goToSsoLogin()}
            href={ssoInformation.ssoClients ? ssoInformation.ssoClients.clientRegistrations[0].fromOidcIssuerLocation : ''}
            isDisabled={isAuthInProgress}
            uniqueID='ssoRedirectHelperLink'
          />
        </div>
      </div>
    )
)

const mapStateToProps = ({loginReducer}) => ({
  authFailedTimes: loginReducer.authFailedTimes,
  codeSendMessage: loginReducer.codeSendMessage,
  codeSendDelay: loginReducer.codeSendDelay,
  mode: loginReducer.mode,
  isPasswordPolicyPending: loginReducer.isPasswordPolicyPending,
  canRequestNewCode: loginReducer.canRequestNewCode,
  isAuthInProgress: loginReducer.isAuthInProgress,
  username: loginReducer.username,
  secretCode: loginReducer.secretCode
})

const mapDispatchToProps = dispatch => ({
  onSwitchMode: mode => dispatch(switchLoginMode(mode)),
  onRequestNewCode: ({username}) => dispatch(requestNewCode({username}))
})

ActionSection.propTypes = {
  isProduction: bool,
  authFailedTimes: number,
  codeSendMessage: string,
  codeSendDelay: number,
  mode: oneOf(loginModesList),
  isPasswordPolicyPending: bool,
  canRequestNewCode: bool,
  onRequestNewCode: func.isRequired,
  onSwitchMode: func.isRequired,
  isAuthInProgress: bool,
  username: string,
  password: string,
  onSubmit: func.isRequired,
  secretCode: string,
  ssoInformation: shape({
    ssoEnabled: bool,
    ssoClients: shape({
      clientRegistrations: shape(arrayOf(shape({
        registrationId: string,
        clientId: string,
        fromOidcIssuerLocation: string
      })))
    })
  })
}

export default connect(mapStateToProps, mapDispatchToProps)(ActionSection)
